import { Await, Link, useNavigate } from '@remix-run/react'
import Avatar from '../avatars/avatar'
import Button from '../buttons/button'
import Counter from '../badgesAndTags/counter'
import SearchInput from '../inputs/searchInput'
import Filter from '../inputs/filter.tsx'
import NftCard from '../content/NftCard'
import {
	TypographyLabel,
	TypographyCaps,
	TypographyDisplay,
	TypographyHeading,
	TypographyParagraph,
} from '../typography'
import {
	CalloutSuccessIcon,
	CalloutWarningIcon,
	CaretLeft,
	MoreIcon,
	OpenInNewIcon,
	SignIcon,
	WebsiteIcon,
} from '../icons'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import {
	getCollectionNFTProfileURL,
	getUserProfileURL,
	scrollToTop,
} from '#app/utils/misc.tsx'
import OffChain from '../common/offChain.tsx'
import ProofOfExhibition from '../common/proofOfExhibition.tsx'
import InternalLink from '../common/internalLink.tsx'
import { SignModal } from '../common/signModal.tsx'
import PillTab from '../tabs/pillTab.tsx'
import IconButton from '../buttons/iconButton.tsx'
import uniqWith from 'lodash.uniqwith'
import isEqual from 'lodash.isequal'
import debounce from 'lodash.debounce'
import Tag from '../badgesAndTags/tag.tsx'
import Loader from '../loaders/loader.tsx'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import clsx from 'clsx'
import {
	AttestationType,
	MariaDBNFTsForCollectionResponse,
	CollectionNFTEntry,
} from '#types/loreTypes.tsx'
import { CollectionType } from '#app/utils/templates/utils/templateTypes.ts'
export default function CollectionProfile({
	isPreview,
	cid,
	artist,
	collection,
	collectionLore,
	nfts,
	nftCount,
	chains,
	origin,
	atomicLoreUrl,
	handleRevalidation,
}: {
	isPreview?: boolean
	cid: string
	artist?: { username: string; userAvatar?: string; userId?: string }
	collection: AttestationType
	collectionLore: Promise<{ attestations: AttestationType[]; count: number }>
	nfts: MariaDBNFTsForCollectionResponse
	nftCount: number
	chains: string[]
	origin: string
	atomicLoreUrl: string
	handleRevalidation: () => void
}) {
	const [signModalOpen, setSignModalOpen] = useState(false)
	const navigate = useNavigate()

	const [isSticky, setIsSticky] = useState(false)

	const stickyRef = useRef<HTMLElement | null>(null)
	const observerRef = useRef<IntersectionObserver | null>(null)
	const aboutRef = useRef(null)
	const signaturesRef = useRef(null)
	const exhibitionsRef = useRef(null)
	const nftsRef = useRef(null)

	useEffect(() => {
		observerRef.current = new IntersectionObserver(
			([entry]) => {
				setIsSticky(!entry.isIntersecting)
			},
			{ threshold: 0.5 },
		)

		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect()
			}
		}
	}, [])

	const updateObservedElement = (element: HTMLElement | null) => {
		if (observerRef.current) {
			if (stickyRef.current) {
				observerRef.current.unobserve(stickyRef.current)
			}
			if (element) {
				observerRef.current.observe(element)
			}
			stickyRef.current = element
		}
	}

	return (
		<>
			<div
				className={`fixed top-0 z-50 flex w-full flex-row justify-between bg-uiTertiaryLight px-4 py-4 text-center transition-opacity duration-500 dark:bg-uiTertiaryDark sm:px-12 ${
					isSticky && !signModalOpen
						? 'opacity-100'
						: 'pointer-events-none opacity-0'
				}`}
			>
				<div className="flex flex-row items-center gap-x-2">
					<Button variant="tertiary" onClick={() => scrollToTop(window)}>
						<TypographyLabel
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{collection.properties.name}
						</TypographyLabel>
					</Button>
					{artist && artist.username && (
						<>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								by
							</TypographyParagraph>
							{artist.userId ? (
								<InternalLink to={getUserProfileURL(artist.username)}>
									<Avatar
										size="xSmall"
										avatar={{ src: artist?.userAvatar, name: artist?.username }}
										isVerified={false}
									/>
								</InternalLink>
							) : (
								<Avatar
									size="xSmall"
									avatar={{ src: artist?.userAvatar, name: artist?.username }}
									isVerified={false}
								/>
							)}
							{artist.userId ? (
								<InternalLink to={getUserProfileURL(artist.username)}>
									<Button variant="tertiary">
										<TypographyLabel
											size="medium"
											isBold={true}
											additionalClasses="hidden sm:flex text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{artist.username}
										</TypographyLabel>
									</Button>
								</InternalLink>
							) : (
								<Button variant="tertiary">
									<TypographyLabel
										size="medium"
										isBold={true}
										additionalClasses="hidden sm:flex text-textPrimaryLight dark:text-textPrimaryDark"
									>
										{artist.username}
									</TypographyLabel>
								</Button>
							)}
						</>
					)}
				</div>
				<div className="flex flex-row items-center gap-x-6">
					<Link to={collection.properties.url} target="_blank">
						<IconButton
							icon={<WebsiteIcon />}
							additionalClasses="border-none"
						/>
					</Link>
					{!isPreview ? (
						<Button
							variant="primary"
							leftIcon={<SignIcon />}
							onClick={() => setSignModalOpen(true)}
							additionalClasses="hidden sm:flex"
						>
							Sign
						</Button>
					) : null}
				</div>
			</div>
			<div className="mx-auto max-w-[1440px] px-4 py-16 md:px-12 lg:px-28">
				{collection && (
					<NavigationHeader collection={collection} isPreview={isPreview} />
				)}{' '}
				<Suspense
					fallback={
						<>
							<CollectionInfo
								isPreview={isPreview}
								cid={cid}
								artist={artist}
								collection={collection}
								collectionLore={{ attestations: [], count: 0 }}
								nftCount={nftCount}
								chains={chains}
								stickyRef={updateObservedElement}
								aboutRef={aboutRef}
								signaturesRef={signaturesRef}
								exhibitionsRef={exhibitionsRef}
								nftsRef={nftsRef}
							/>
							<OffChain
								atomicLoreUrl={atomicLoreUrl}
								timeline={[]}
								isPreview={isPreview}
							/>
							<ProofOfExhibition exhibitions={[]} />
						</>
					}
				>
					<Await resolve={collectionLore}>
						{collectionLore => {
							return (
								<>
									<CollectionInfo
										isPreview={isPreview}
										cid={cid}
										artist={artist}
										collection={collection}
										collectionLore={collectionLore}
										nftCount={nftCount}
										chains={chains}
										stickyRef={updateObservedElement}
										aboutRef={aboutRef}
										signaturesRef={signaturesRef}
										exhibitionsRef={exhibitionsRef}
										nftsRef={nftsRef}
									/>
									<div ref={signaturesRef}>
										<OffChain
											showSignButton={!isPreview}
											setSignModalOpen={setSignModalOpen}
											atomicLoreUrl={atomicLoreUrl}
											timeline={collectionLore.attestations}
											isPreview={isPreview}
											subtitle={
												'View a timeline of all signed history in this Collection'
											}
											noContentText={
												'No history has been signed yet. Tap "Sign" to add onchain lore to this Collection.'
											}
										/>
									</div>
									<div ref={exhibitionsRef}>
										<ProofOfExhibition
											showSignButton={!isPreview}
											setSignModalOpen={setSignModalOpen}
											exhibitions={collectionLore.attestations.filter(
												attestation =>
													attestation.template.slug == 'proofOfExhibition',
											)}
											subtitle={
												'To document an exhibition to Collection, click "Sign" and select an Exhibition template.'
											}
										/>
									</div>
								</>
							)
						}}
					</Await>
				</Suspense>
				<div ref={nftsRef}>
					<Suspense
						fallback={
							<NFTs
								nfts={[]}
								nftsCount={0}
								isInSuspense={true}
								collection={collection}
								chains={chains}
							/>
						}
					>
						<Await resolve={nfts}>
							{nfts => {
								let passedNFTs: CollectionNFTEntry[] = []
								if (Array.isArray(nfts.nfts)) {
									passedNFTs = nfts.nfts
								}
								return (
									<NFTs
										nfts={passedNFTs}
										isInSuspense={false}
										nftMessage={nfts.message}
										nftsCount={nftCount}
										chains={chains}
										collection={collection}
									/>
								)
							}}
						</Await>
					</Suspense>
				</div>
				<SignModal
					open={signModalOpen}
					onClose={() => {
						setSignModalOpen(false)
						handleRevalidation()
					}}
					collection={collection}
					origin={origin}
					atomicLoreUrl={atomicLoreUrl}
				/>
			</div>
		</>
	)
}

const NavigationHeader = ({
	collection,
	isPreview,
}: {
	collection: AttestationType
	isPreview?: boolean
}) => {
	return (
		<div
			className={clsx(
				isPreview ? 'mb-20' : 'mb-24',
				'flex px-0 md:flex-row md:items-center lg:px-12 ',
			)}
		>
			<div className="flex flex-col items-center justify-around gap-y-4 pl-0 md:items-start lg:justify-evenly">
				<InternalLink to={`/`} prefetch="none">
					<Button variant="tertiary">
						<div className="flex items-center">
							<CaretLeft />
							Home
						</div>
					</Button>
				</InternalLink>
			</div>
			<div className="ml-auto mr-0 flex items-center justify-between space-x-4 lg:mt-0">
				{collection.properties.url && (
					<Link to={collection.properties.url} target="_blank">
						<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
							<WebsiteIcon />
						</div>
					</Link>
				)}
			</div>
		</div>
	)
}

const CollectionInfo = ({
	isPreview,
	cid,
	artist,
	collection,
	collectionLore,
	nftCount,
	chains,
	stickyRef,
	aboutRef,
	signaturesRef,
	exhibitionsRef,
	nftsRef,
}: {
	isPreview?: boolean
	cid: string
	artist?: { username?: string; userAvatar?: string; userId?: string }
	collection: AttestationType
	collectionLore: { attestations: AttestationType[]; count: number }
	nftCount: number
	chains: string[]
	stickyRef: any
	aboutRef: any
	signaturesRef: any
	exhibitionsRef: any
	nftsRef: any
}) => {
	const [selectedSection, setSelectedSection] = useState('About')
	const navigate = useNavigate()

	return (
		<div className="flex flex-col px-0 lg:px-12">
			{isPreview && (
				<div className="mb-12 w-full">
					<div className="flex items-center justify-between rounded-md border border-yellow bg-[#FFFBF1] dark:border dark:border-solid dark:border-[#D47307] dark:bg-[#FFFBF11A] dark:text-textPrimaryDark	">
						<div className="mx-4 my-4 flex gap-x-4">
							<div className="my-auto h-full">
								<CalloutWarningIcon />
							</div>
							<div className="flex flex-col gap-y-2">
								<div className="flex flex-row gap-x-2">
									<TypographyParagraph size="medium" isBold={true}>
										Preview Mode
									</TypographyParagraph>
									<Tag variant="outline" showIcon={false}>
										Draft
									</Tag>
								</div>
								<TypographyParagraph size="medium">
									This is how your collection will appear after signing.{' '}
								</TypographyParagraph>
							</div>
						</div>
						<Button
							variant="primary"
							additionalClasses="mr-4"
							size="medium"
							onClick={() => {
								navigate(`/collectionEdit/${cid}`)
							}}
						>
							Edit Collection
						</Button>
					</div>
				</div>
			)}
			<div className="flex flex-col items-center justify-center">
				{collection.properties.name && (
					<>
						<TypographyCaps
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Collection
						</TypographyCaps>
						<TypographyDisplay
							size="large"
							additionalClasses="mt-4 text-center justify-center text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{collection.properties.name}
						</TypographyDisplay>
					</>
				)}
				<div ref={stickyRef}>
					{artist && artist.userId ? (
						<InternalLink
							additionalClasses="mb-24 mt-4 flex md:flex-row md:items-center"
							to={getUserProfileURL(artist.username)}
						>
							<Avatar
								size="small"
								avatar={{
									src: artist.userAvatar,
									name: artist.username,
								}}
							/>
							<div className="flex flex-col justify-around pl-4 md:items-start lg:justify-evenly">
								<TypographyLabel
									size="medium"
									isBold={true}
									additionalClasses="dark:text-textPrimaryDark"
								>
									{artist.username}
								</TypographyLabel>
								<TypographyLabel
									size="small"
									isBold={true}
									additionalClasses="text-textSecondaryLight ark:text-textSecondaryDark"
								>
									Artist
								</TypographyLabel>
							</div>
						</InternalLink>
					) : artist && artist.username ? (
						<div className="mb-24 mt-4 flex md:flex-row md:items-center">
							<Avatar
								size="small"
								avatar={{
									src: artist.userAvatar,
									name: artist.username,
								}}
							/>
							<div className="flex flex-col justify-around pl-4 md:items-start lg:justify-evenly">
								<TypographyLabel
									size="medium"
									isBold={true}
									additionalClasses="dark:text-textPrimaryDark"
								>
									{artist.username}
								</TypographyLabel>
								<TypographyLabel
									size="small"
									isBold={true}
									additionalClasses="text-textSecondaryLight ark:text-textSecondaryDark"
								>
									Artist
								</TypographyLabel>
							</div>
						</div>
					) : (
						<div className="mb-24 mt-4"></div>
					)}
				</div>
				<img
					className="h-auto max-h-[390px] w-full object-contain sm:max-h-[720px]"
					src={
						(collection.properties as CollectionType).web2URL
							? (collection.properties as CollectionType).web2URL
							: (collection.properties as CollectionType).imageUrl
					}
				/>
			</div>

			<div className="mt-12 flex w-full flex-row items-center justify-center sm:gap-x-4">
				<PillTab
					title="About"
					selected={selectedSection == 'About'}
					onClick={() => {
						setSelectedSection('About')
						window && scrollToTop(window, aboutRef)
					}}
				/>
				<PillTab
					title="Signatures"
					selected={selectedSection == 'Signatures'}
					onClick={() => {
						setSelectedSection('Signatures')
						window && scrollToTop(window, signaturesRef)
					}}
				/>
				<PillTab
					title="Exhibitions"
					selected={selectedSection == 'Exhibitions'}
					onClick={() => {
						setSelectedSection('Exhibitions')
						window && scrollToTop(window, exhibitionsRef)
					}}
				/>
				<PillTab
					title="NFTs"
					selected={selectedSection == 'NFTs'}
					onClick={() => {
						setSelectedSection('NFTs')
						window && scrollToTop(window, nftsRef)
					}}
				/>
			</div>

			<div className="flex flex-col px-0" ref={aboutRef}>
				<CollectionStatTable
					cid={cid}
					collection={collection}
					collectionLore={collectionLore}
					chains={chains}
					nftCount={nftCount}
					isPreview={isPreview}
				/>
				{collection.properties.description && (
					<div className="mt-24">
						<TypographyLabel
							size="large"
							additionalClasses="text-black dark:text-textPrimaryDark"
						>
							About this Collection
						</TypographyLabel>
						<TypographyHeading
							size="medium"
							additionalClasses="mt-8 text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{collection.properties.description}
						</TypographyHeading>
					</div>
				)}
			</div>
		</div>
	)
}

const CollectionStatTable = ({
	cid,
	collection,
	collectionLore,
	nftCount,
	chains,
	isPreview = false,
}: {
	cid: string
	collection: AttestationType
	collectionLore?: { attestations: AttestationType[]; count: number }
	nftCount: number
	chains: string[]
	isPreview?: boolean
}) => {
	const tooltipProvenance = useRef<TooltipRefProps>(null)

	return (
		<div className="mt-24 inline-block w-full overflow-x-auto rounded-lg border border-textPrimaryLight align-middle dark:border-textPrimaryDark">
			<table className="min-w-full divide-y divide-textPrimaryLight dark:divide-textPrimaryDark">
				<thead>
					<tr className="divide-x divide-textPrimaryLight dark:divide-textPrimaryDark">
						<th
							scope="col"
							className="py-3.5 pl-4 pr-4 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark"
						>
							<TypographyCaps size="small">Items</TypographyCaps>
						</th>
						<th
							scope="col"
							className="px-4 py-3.5 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark"
						>
							<TypographyCaps size="small">Created</TypographyCaps>
						</th>
						<th
							scope="col"
							className="px-4 py-3.5 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark"
						>
							<TypographyCaps size="small">Chain</TypographyCaps>
						</th>
						<th
							scope="col"
							className="px-4 py-3.5 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark"
						>
							<TypographyCaps size="small">Signatures</TypographyCaps>
						</th>
						<th
							scope="col"
							className="px-4 py-3.5 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark"
						>
							<TypographyCaps size="small">Blockchain status</TypographyCaps>
						</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-textPrimaryLight dark:divide-textPrimaryDark">
					<tr className="divide-x divide-textPrimaryLight dark:divide-textPrimaryDark">
						<td className="whitespace-nowrap py-4 pl-4  pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
							<TypographyParagraph size="large" isBold={true}>
								{nftCount}
							</TypographyParagraph>
						</td>
						<td className="whitespace-nowrap py-4 pl-4  pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
							<TypographyParagraph size="large" isBold={true}>
								{collection.properties.date
									? collection.properties.date
									: 'Unknown'}
							</TypographyParagraph>
						</td>
						<td className="whitespace-nowrap py-4 pl-4  pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
							<TypographyParagraph size="large" isBold={true}>
								{Array.from(new Set(chains.filter(name => name !== undefined)))
									.join(', ')
									.toUpperCase()}
							</TypographyParagraph>
						</td>
						<td className="whitespace-nowrap py-4 pl-4  pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
							<TypographyParagraph size="large" isBold={true}>
								{collectionLore ? collectionLore.count : 'Loading...'}
							</TypographyParagraph>
						</td>
						<td className="whitespace-nowrap py-4 pl-4  pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
							{isPreview ? (
								<>
									<div
										id="tooltipProvenance"
										className="flex cursor-pointer flex-row items-center justify-center gap-x-2"
										onClick={() => {
											tooltipProvenance.current?.open({
												anchorSelect: '#tooltipProvenance',
												content:
													'This will link to the provenance page once the collection is signed.',
											})
											setTimeout(() => {
												tooltipProvenance.current?.close()
											}, 3000)
										}}
									>
										<TypographyParagraph size="large" isBold={true}>
											Offchain
										</TypographyParagraph>
									</div>
									<Tooltip imperativeModeOnly ref={tooltipProvenance} />
								</>
							) : (
								<InternalLink
									to={`/metadataProfile/collection/${cid}`}
									additionalClasses="flex flex-row items-center justify-center gap-x-2"
								>
									<CalloutSuccessIcon />
									<TypographyParagraph size="large" isBold={true}>
										Onchain
									</TypographyParagraph>
									<OpenInNewIcon width="12" height="12" />
								</InternalLink>
							)}
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	)
}

const NFTs = ({
	nfts,
	nftMessage,
	nftsCount,
	chains,
	isInSuspense,
	collection,
}: {
	nfts: CollectionNFTEntry[]
	isInSuspense: boolean
	nftMessage?: string
	nftsCount: number
	chains: string[]
	collection: AttestationType
}) => {
	const navigate = useNavigate()
	const [clientNFTs, setClientNFTs] = useState(nfts)
	const [serverCount, setServerCount] = useState(nftsCount)
	const [isLoading, setIsLoading] = useState(false)
	const [nameFilter, setNameFilter] = useState('')
	const [debouncedNameFilter, setDebouncedNameFilter] = useState('')
	const [chainFilter, setChainFilter] = useState({
		title: 'All types',
		description: 'All types',
	})

	const handleUpdateDebouncedNameFilter = (value: string) => {
		setDebouncedNameFilter(value)
	}

	const debouncedNameFilterchange = useMemo(
		() => debounce(handleUpdateDebouncedNameFilter, 1000),
		[],
	)

	const options = [
		{ title: 'All types', description: 'All types' },
		{ title: 'Ethereum', description: 'Ethereum' },
		{ title: 'Solana', description: 'Solana' },
		{ title: 'Base', description: 'Base' },
		{ title: 'Tezos', description: 'Tezos' },
		{ title: 'Zora', description: 'Zora' },
		{ title: 'Polygon', description: 'Polygon' },
		{ title: 'Shape', description: 'Shape' },
	]

	const filterByName = (nfts: CollectionNFTEntry[]) => {
		return nameFilter
			? nfts.filter(nft =>
					nft.name.toLowerCase().includes(nameFilter.toLowerCase()),
				)
			: nfts
	}

	const filterByDebouncedName = (nfts: CollectionNFTEntry[]) => {
		return debouncedNameFilter
			? nfts.filter(nft =>
					nft.name.toLowerCase().includes(debouncedNameFilter.toLowerCase()),
				)
			: nfts
	}

	const filterByChain = (nfts: CollectionNFTEntry[]) => {
		if (!chainFilter) return nfts
		return chainFilter.title != 'All types'
			? nfts.filter(
					nft =>
						nft.chainName &&
						nft.chainName
							.toLowerCase()
							.includes(chainFilter.title.toLowerCase()),
				)
			: nfts
	}

	const filteredNFTs = () => {
		return filterByName(filterByChain(clientNFTs))
	}

	const debounceFilteredNFTs = () => {
		return filterByDebouncedName(filterByChain(clientNFTs))
	}

	useEffect(() => {
		const filteredNFTLength = debounceFilteredNFTs().length
		if (filteredNFTLength === clientNFTs.length) {
			if (serverCount !== nftsCount) {
				setServerCount(nftsCount)
			}
		}
		if (chainFilter.title !== 'All types' || debouncedNameFilter !== '') {
			if (filteredNFTLength < clientNFTs.length) {
				loadNFTs()
			}
		}
	}, [chainFilter.title, debouncedNameFilter])

	const loadNFTs = async () => {
		setIsLoading(true)
		try {
			const options = {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			}
			const filteredNFTLength = debounceFilteredNFTs().length
			const calculatedPage = Math.floor(filteredNFTLength / 100)
			const requestURL = `/collectionNFTAPI?collectionCid=${collection.references.attestationIPFSCID}&page=${calculatedPage}&sortOrder=ASC${chainFilter.title !== 'All types' ? `&chain=${chainFilter.title.toLowerCase()}` : ''}${debouncedNameFilter !== '' ? `&name=${debouncedNameFilter}` : ''}`
			const response = await fetch(requestURL, options)
			const nftResponse = (await response.json()) as {
				cid?: string
				page: number
				nfts: CollectionNFTEntry[]
				count: number
			}
			if (nftResponse && nftResponse.cid) {
				if (nftResponse.nfts.length > 0) {
					const newClientNFTs = Array.from([...clientNFTs]).concat(
						nftResponse.nfts,
					)
					const dedupedNFTs = uniqWith(newClientNFTs, isEqual)
					setClientNFTs(dedupedNFTs)
				}
				setServerCount(nftResponse.count)
			}
		} catch (e) {
			console.error('Load NFTs Error', {
				e,
				chainFilter,
				nameFilter,
			})
		}
		setIsLoading(false)
	}

	const canFetchNextPage = () => {
		if (nftMessage && nftMessage === 'Indexing Collection') {
			return false
		} else if (nfts.length === 0) {
			return false
		} else {
			return clientNFTs.length === 0 || clientNFTs.length < serverCount
		}
	}

	const hasIndexingNFTMessage = () => {
		return nftMessage !== undefined && nftMessage !== ''
	}

	return (
		<div className="my-24 flex w-full flex-col gap-y-4 px-0 lg:px-12">
			<div className="mb-8 w-full justify-between sm:flex sm:flex-row">
				<div className="flex flex-1 items-center gap-x-4">
					<TypographyHeading
						size="medium"
						isBold={true}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						Artworks
					</TypographyHeading>
					<Counter size="small" children={nftsCount} />
				</div>
				<div className="mt-3 flex flex-1 items-center justify-start gap-x-4 sm:mt-0 sm:justify-end">
					<SearchInput
						name="Search"
						placeholder="Search"
						showShadow={false}
						value={nameFilter}
						isInactive={false}
						setValue={newVal => {
							setNameFilter(newVal)
							debouncedNameFilterchange(newVal)
						}}
						containerWidth="w-80 hidden lg:!block"
					/>
					<Filter
						type="Chain"
						options={options.filter(
							option =>
								option.title === 'All types' ||
								chains.indexOf(option.title.toLowerCase()) >= 0,
						)}
						onChange={newVal => setChainFilter(newVal)}
					></Filter>
				</div>
			</div>
			<div className="-mx-4 flex grid grid-cols-2 flex-wrap sm:!grid-cols-3 lg:!grid-cols-4">
				{filteredNFTs()
					.sort((a, b) => {
						if (a.tokenId < b.tokenId) {
							return -1
						} else {
							return 1
						}
					})
					.map((nft: CollectionNFTEntry, index) => (
						<InternalLink
							key={`${nft.name}-${nft.tokenId}-${index}`}
							to={getCollectionNFTProfileURL(nft)}
							prefetch="none"
						>
							<div key={nft.name} className="mb-8 w-full px-4">
								<div className="w-full">
									<NftCard
										name={nft.name}
										image={nft.image}
										collectionName={collection.properties.name}
										chain={nft.chainName}
										contract={nft.contractAddress}
										tokenId={nft.tokenId}
										cardAdditionalProps="h-full w-auto"
										containerAdditionalProps="relative w-full pt-[100%] bg-grey100"
										imageAdditionalProps="absolute top-0 left-0 w-full h-full object-cover"
									/>
								</div>
							</div>
						</InternalLink>
					))}
			</div>
			{canFetchNextPage() ? (
				isLoading ? (
					<div className="mt-6 flex w-full items-center justify-center">
						<Loader size="small" />
					</div>
				) : (
					<div className="mt-6 flex w-full items-center justify-center">
						<Tag
							showIcon={false}
							disabled={isLoading}
							onClick={() => {
								if (!isLoading) loadNFTs()
							}}
						>
							<MoreIcon />
						</Tag>
					</div>
				)
			) : null}
			{nfts.length === 0 && !hasIndexingNFTMessage() && !isInSuspense && (
				<div className="mx-auto mt-6 flex w-full max-w-sm flex-col items-center justify-center">
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark mb-4"
					>
						We're still indexing this Collection, please reload the page to see
						NFTs appear. It will take longer for collections with full contracts
						but if they still haven't appeared after a minute, please contact
						support
					</TypographyParagraph>
					<Button
						variant="primary"
						size="large"
						onClick={() => {
							navigate('.', { replace: true })
						}}
					>
						Reload Page
					</Button>
				</div>
			)}
			{hasIndexingNFTMessage() ? (
				isLoading ? (
					<div className="mt-6 flex w-full items-center justify-center">
						<Loader size="small" />
					</div>
				) : (
					<div className="mx-auto mt-6 flex w-full max-w-sm flex-col items-center justify-center">
						{nftMessage === 'Indexing Collection' && (
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark mb-4"
							>
								We're currently indexing this Collection, please wait a minute
								then reload the page to see NFTs appear. It will take longer for
								collections with full contracts but if they still haven't
								appeared after a minute, please contact support
							</TypographyParagraph>
						)}
						<Button
							variant="primary"
							size="large"
							onClick={() => {
								navigate('.', { replace: true })
							}}
						>
							Reload Page
						</Button>
					</div>
				)
			) : null}
		</div>
	)
}
